import { makeObservable, observable, action, runInAction } from '../../../common/stores/mobx-config.js';
import { infoWindowDisplayManager } from '../managers/infoWindowDisplayManager.js';
import { layerManager } from '../managers/layerManager.js';
import BaseLayerStore, { createClusterBadge } from './baseLayerStore.js';
import { getCoordinatesForTown } from '../../../utils/coordinatesLookup.js';
import familyTownsStore from './familyTownsStore.js';
import { logger } from '../../../utils/debug/logger.js';
// Phase 8.2 Fix: Import pour extraire birthTownKey depuis les événements
import { getBirthTownKey, getBirthYear, getName, getSurname } from '../../../gedcom/formatters/individualFormatters.js';
import { getBranchFromSosa } from '../../../common/utils/branchUtils.js';

/**
 * Manages the display of ancestor birth locations on the map for the current root person
 * Each time a new person is selected as root, this store processes their ancestor tree
 * to display birth locations with different colors for paternal and maternal lines
 * 
 * Key features:
 * - Processes birth locations from the root person's ancestor tree
 * - Colors markers based on lineage (blue for paternal, pink for maternal, purple for mixed)
 * - Handles marker clustering for dense areas
 * - Updates dynamically when root person changes
 */
class RootAncestorTownsStore extends BaseLayerStore {
    constructor() {
        super('ancestors');
        this.markerLayerName = 'rootAncestors';

        // Core data
        this.birthData = [];

        // Style configuration for different branches
        this.styles = {
            colors: {
                paternal: '#1e40af',  // Blue for paternal line
                maternal: '#be185d',  // Pink for maternal line
                mixed: '#9333ea'      // Purple for mixed locations
            }
        };

        makeObservable(this, {
            birthData: observable,
            map: observable.ref,
            updateMarkers: action,
            clearMarkers: action
            // processHierarchy n'est PAS une action MobX : c'est une méthode async,
            // et une action ne couvre pas les mutations après un await. Ses mutations
            // observables passent par runInAction / par les actions updateMarkers &
            // clearMarkers qu'elle appelle.
        });
    }

    /**
     * Process the entire genealogical hierarchy
     * @param {Object} hierarchy - Family tree hierarchy
     * @returns {Array} Processed birth locations
     */
    async processHierarchy(hierarchy) {
        try {
            logger.map.ancestors.group('📍 Processing ancestor towns hierarchy');

            // Nettoyer explicitement d'abord
            this.clearMarkers();

            if (!hierarchy) {
                logger.map.ancestors.warn('⚠️ Invalid or missing hierarchy');
                logger.map.ancestors.groupEnd();
                return;
            }

            const birthData = [];

            // Recursive function to process each node in the hierarchy
            const processNode = (node, depth = 0) => {
                // Phase 8.2 Fix: Extraire birthTownKey depuis les événements
                const birthTownKey = getBirthTownKey(node);
                
                if (birthTownKey) {
                    // Phase 8.2: birthTownKey est déjà normalisé, lookup direct
                    const coordinates = getCoordinatesForTown(birthTownKey);
                    const townData = familyTownsStore.getTownsData(birthTownKey);
                    
                    if (coordinates) {
                        birthData.push({
                            id: node.id,
                            name: `${getName(node)} ${getSurname(node)}`,
                            birthYear: getBirthYear(node), // Phase 8.2: Extraire depuis événements
                            generation: node.generation || 0,
                            sosa: node.sosa || 1,
                            location: {
                                lat: parseFloat(coordinates.latitude),
                                lng: parseFloat(coordinates.longitude),
                                name: townData?.town || birthTownKey,
                                departement: townData?.departement || ''
                            }
                        });
                    } else {
                        logger.map.ancestors.warn(`⚠️ Pas de coordonnées trouvées pour ${birthTownKey} (${getName(node)} ${getSurname(node)})`);
                    }
                } else {
                    // Donnée manquante normale - pas besoin de logger
                    // logger.map.ancestors.debug(`ℹ️ Pas de lieu de naissance pour ${getName(node)} ${getSurname(node)}`);
                }

                // Process children recursively
                if (node.children && Array.isArray(node.children)) {
                    node.children.forEach(child => processNode(child, depth + 1));
                }
            };

            processNode(hierarchy);

            logger.map.ancestors.info(`✅ Birth data extracted for ${birthData.length} locations`);
            runInAction(() => { this.birthData = birthData; });

            // Vérifier si le calque est visible et afficher les marqueurs si c'est le cas
            if (birthData.length > 0 && layerManager.isLayerVisible(this.layerName)) {
                logger.map.ancestors.info('🔄 Mise à jour automatique des marqueurs après changement de hiérarchie');
                await this.updateMarkers(birthData);

                // Remplacer cette ligne
                logger.map.ancestors.info('🔍 Ajustement du zoom pour afficher tous les marqueurs');
                this.centerMapOnAncestorMarkers();
            }

            logger.map.ancestors.groupEnd();
            return birthData;
        } catch (error) {
            logger.map.ancestors.error('❌ Error processing hierarchy:', error);
            logger.map.ancestors.groupEnd();
            throw error;
        }
    }

    /**
     * Create a cluster marker for grouped locations
     * @param {Object} param0 - Cluster parameters
     * @param {number} param0.count - Number of markers in cluster
     * @param {google.maps.LatLng} param0.position - Cluster position
     */
    createClusterMarker({ count, position }) {
        return createClusterBadge({
            count,
            position,
            color: this.styles.colors.mixed,
            size: Math.min(count * 3, 20) * 2,
            zIndex: 1000
        });
    }

    /**
     * Centre la carte sur les marqueurs des ancêtres
     * Utilise la méthode générique de BaseLayerStore pour un comportement cohérent
     */
    centerMapOnAncestorMarkers() {
        // Utiliser la méthode générique standardisée
        this.centerMapOnMarkers();
    }


    /**
     * Update all markers on the map
     * @param {Array} birthData - Array of birth location data
     */
    async updateMarkers(birthData) {
        if (!this.map || !birthData?.length) {
            logger.map.ancestors.warn('⚠️ Carte ou données absentes');
            return;
        }

        logger.map.ancestors.debug(`🔄 Mise à jour des marqueurs pour ${birthData.length} lieux.`);

        // 1. S'assurer que le nettoyage est vraiment fait
        this.clearMarkers();

        // 2. Vérifier que le nettoyage a bien fonctionné
        const layerExists = this.markerDisplayManager.layers.has(this.markerLayerName);
        if (layerExists) {
            logger.map.ancestors.warn('⚠️ La couche existe encore après nettoyage, forçage du nettoyage...');
            this.markerDisplayManager.layers.delete(this.markerLayerName);
        }

        // 3. Mettre à jour les données
        this.birthData = birthData;

        // 4. Continuer avec le reste de la logique...
        const locationMap = this.groupBirthDataByLocation(birthData);
        logger.map.ancestors.debug(`📍 Nombre de lieux uniques: ${locationMap.size}`);

        locationMap.forEach((locationData) => {
            if (!this.isValidLocationData(locationData)) return;

            // Utiliser getOrCreateMarker au lieu de addMarker
            this.markerDisplayManager.getOrCreateMarker(
                this.markerLayerName,
                locationData.location.name,
                {
                    latitude: locationData.location.lat,
                    longitude: locationData.location.lng,
                    townDisplay: locationData.location.name
                },
                (data) => this.createMarkerElement(locationData),
                (marker) => {
                    const content = this.createInfoWindowContent(
                        locationData.location,
                        locationData.births,
                        locationData.generations
                    );
                    infoWindowDisplayManager.showInfoWindow(marker, content);
                }
            );
        });

        if (layerManager.isLayerVisible('ancestors')) {
            this.markerDisplayManager.toggleLayerVisibility(this.markerLayerName, true, this.map);
        }
    }

    isValidLocationData(locationData) {
        // Number.isFinite (et non `lat && lng`) : accepte la latitude/longitude 0
        // (équateur / méridien de Greenwich) et rejette NaN issu d'un parseFloat raté.
        return Number.isFinite(locationData?.location?.lat)
            && Number.isFinite(locationData?.location?.lng);
    }

    createMarkerElement(locationData) {
        const div = document.createElement('div');
        div.className = 'custom-marker';

        // Ajouter un ID au premier marqueur pour le tour
        if (!document.getElementById('rootMarkerForTour')) {
            div.id = 'rootMarkerForTour';
        }

        div.style.cssText = `
            background: ${this.getBranchColor(locationData.births)};
            border-radius: 50%;
            width: 16px;
            height: 16px;
            border: 1px solid white;
            transition: transform 0.3s ease;
        `;
        return div;
    }

    clearMarkers() {
        logger.map.ancestors.debug('🧹 Nettoyage complet des marqueurs d\'ancêtres');

        // 1. Réinitialiser les données d'abord
        this.birthData = [];

        // 2. S'assurer que les marqueurs sont retirés de la carte avant tout
        if (this.markerDisplayManager && this.markerDisplayManager.layers) {
            const layer = this.markerDisplayManager.layers.get(this.markerLayerName);
            if (layer) {
                // Rendre tous les marqueurs invisibles d'abord
                layer.forEach(marker => {
                    marker.map = null;
                });
            }
        }

        // 3. Demander au gestionnaire de marqueurs de nettoyer complètement
        this.markerDisplayManager.clearMarkers(this.markerLayerName);

        // 4. Rafraîchir le clustering via l'API dédiée (cluster.render()) plutôt
        //    qu'en simulant un 'zoom_changed' sur la carte, qui déclenchait aussi
        //    par effet de bord les autres écouteurs zoom_changed (inset map, etc.).
        this.markerDisplayManager.refreshCluster();

        logger.map.ancestors.debug('✅ Nettoyage des marqueurs d\'ancêtres terminé');
    }


    /**
 * Hook: Mise à jour des marqueurs du calque
 * Spécifique à RootAncestorTownsStore: mise à jour des marqueurs avec birthData
 */
    updateLayerMarkers() {
        if (this.birthData && this.birthData.length > 0) {
            logger.map.ancestors.debug(`🔄 Mise à jour des marqueurs d'ancêtres pour ${this.birthData.length} lieux.`);
            this.updateMarkers(this.birthData);
        } else {
            logger.map.ancestors.debug('ℹ️ Pas de données d\'ancêtres à afficher.');
        }
    }

    /**
 * Hook: Actions après affichage du calque
 * Centre automatiquement la carte sur les marqueurs d'ancêtres
 */
    afterLayerShown() {
        logger.map.ancestors.debug('🔄 Calque des ancêtres affiché, centrage automatique');
        // Vérifier qu'il y a des marqueurs avant de tenter le centrage
        if (this.birthData && this.birthData.length > 0) {
            logger.map.ancestors.debug(`📊 Centrage sur ${this.birthData.length} lieux d'ancêtres`);
            this.centerMapOnAncestorMarkers();
        } else {
            logger.map.ancestors.debug('⚠️ Pas de données d\'ancêtres pour le centrage');
        }
    }

    /**
     * Group birth data by location
     * @param {Array} data - Birth data array
     * @returns {Map} Grouped data by location
     */
    groupBirthDataByLocation(data) {
        return new Map(
            data.reduce((acc, birth) => {
                if (!birth.location?.lat || !birth.location?.lng || !birth.location?.name) {
                    logger.map.ancestors.warn(`Invalid location data for ${birth.name}`);
                    return acc;
                }

                const key = `${birth.location.lat}-${birth.location.lng}-${birth.location.name}`;
                const existing = acc.get(key) || {
                    location: birth.location,
                    births: [],
                    generations: {}
                };

                existing.births.push(birth);
                existing.generations[birth.generation] = [...(existing.generations[birth.generation] || []), birth];

                return acc.set(key, existing);
            }, new Map())
        );
    }

    createInfoWindowContent(location, births, generations) {
        const div = document.createElement('div');
        div.className = 'info-window-content';

        const title = document.createElement('h3');
        title.textContent = location.name;
        title.className = 'font-bold text-lg mb-2';
        div.appendChild(title);

        if (location.departement) {
            const dept = document.createElement('p');
            dept.textContent = `Département: ${location.departement}`;
            dept.className = 'text-sm text-gray-600 mb-2';
            div.appendChild(dept);
        }

        const birthsByGeneration = this.groupBirthsByGeneration(births);
        birthsByGeneration.forEach((birthsInGen, gen) => {
            const genDiv = document.createElement('div');
            genDiv.className = 'generation-group mb-2';

            const genTitle = document.createElement('h4');
            genTitle.textContent = `Génération ${gen}`;
            genTitle.className = 'font-semibold text-sm text-gray-700';
            genDiv.appendChild(genTitle);

            const list = document.createElement('ul');
            list.className = 'list-disc ml-4';

            birthsInGen.forEach(birth => {
                const li = document.createElement('li');
                li.className = 'text-sm';

                const branch = this.determineBranchFromSosa(birth.sosa);
                const color = branch === 'paternal' ? this.styles.colors.paternal :
                    branch === 'maternal' ? this.styles.colors.maternal :
                        this.styles.colors.mixed;

                li.style.color = color;
                li.textContent = birth.name;
                if (birth.birthYear) {
                    li.textContent += ` (${birth.birthYear})`;
                }

                list.appendChild(li);
            });

            genDiv.appendChild(list);
            div.appendChild(genDiv);
        });

        return div;
    }

    groupBirthsByGeneration(births) {
        const grouped = new Map();

        births.forEach(birth => {
            const generation = birth.generation;
            if (!grouped.has(generation)) {
                grouped.set(generation, []);
            }
            grouped.get(generation).push(birth);
        });

        return new Map([...grouped.entries()].sort((a, b) => a[0] - b[0]));
    }

    /**
     * Determine branch type from SOSA number. Delegates to the shared, correct
     * implementation (the branch is the SECOND-most-significant bit of the Sosa,
     * NOT its parity — parity is gender). Sosa 1 (root) and invalid → 'unknown'.
     * @param {number} sosa - SOSA-Stradonitz number
     * @returns {string} Branch type ('paternal', 'maternal', or 'unknown')
     */
    determineBranchFromSosa(sosa) {
        if (!sosa || sosa < 2) return 'unknown';
        return getBranchFromSosa(sosa).branch; // 'paternal' | 'maternal'
    }

    /**
     * Get color based on branch types in a location
     * @param {Array} births - Birth events at location
     * @returns {string} Color code for marker
     */
    getBranchColor(births) {
        if (!births || births.length === 0) return this.styles.colors.mixed;

        const branches = new Set(
            births.map(birth => this.determineBranchFromSosa(birth.sosa))
                .filter(branch => branch !== 'unknown')
        );

        if (branches.size === 0) return this.styles.colors.mixed;
        if (branches.size === 1) {
            return branches.has('paternal') ?
                this.styles.colors.paternal :
                this.styles.colors.maternal;
        }
        return this.styles.colors.mixed;
    }

    // Surcharge la méthode cleanup pour effectuer des nettoyages spécifiques
    cleanup() {
        this.clearMarkers();
        super.cleanup(); // Appel à la méthode cleanup de la classe parente
    }
}

export const rootAncestorTownsStore = new RootAncestorTownsStore();